import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Trash2, Clock, Image as ImageIcon, Edit3 } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";

// Admin "Media" tab — queue of pending photo submissions plus a flat view
// of already-approved photos so an admin can delete something that slipped
// through. The user-facing /media page has the same tools inline, this
// tab is just the dashboard flavour so admins who live in /admin don't
// have to leave the page.

const resolveUrl = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

export function MediaSection() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");

  const reload = useCallback(async () => {
    try {
      const res = await api.get("/media");
      setState(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load media.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (loading) {
    return (
      <div className="font-mono-ui text-xs uppercase tracking-[0.25em] text-[#5C6B51] animate-pulse">
        // Loading media…
      </div>
    );
  }
  if (!state) return null;

  const approvedFlat = (state.years || []).flatMap((g) => g.items);
  const tabs = [
    { id: "pending", label: `Pending (${state.pending.length})` },
    { id: "approved", label: `Approved (${approvedFlat.length})` },
  ];

  return (
    <div data-testid="media-editor">
      <div className="flex items-center justify-between">
        <div>
          <span className="label-tag text-[#C0A875]">// GALLERY / Review queue</span>
          <h3 className="mt-1 font-display uppercase text-[#E2E4DF] text-2xl">Media Submissions</h3>
          <p className="mt-1 font-mono-ui text-[11px] uppercase tracking-[0.2em] text-[#8D9A87]">
            Survivor-uploaded photos. Approve or reject, or delete anything already live.
          </p>
        </div>
        {!state.is_live && (
          <span
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#C0A875]/50 text-[#C0A875] font-mono-ui text-[10px] uppercase tracking-[0.25em]"
            title="The /media page is still gated for non-admins (coming-soon screen)."
          >
            <Clock size={11} /> WIP — hidden from non-admins
          </span>
        )}
      </div>

      <div className="mt-6 flex gap-2 border-b border-[#283524]">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            data-testid={`media-editor-tab-${t.id}`}
            className={`px-4 py-2 font-mono-ui text-[11px] uppercase tracking-[0.2em] border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? "border-[#C0A875] text-[#C0A875]"
                : "border-transparent text-[#8D9A87] hover:text-[#E2E4DF]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "pending" && (
          <PendingList items={state.pending} onChange={reload} />
        )}
        {tab === "approved" && (
          <ApprovedList items={approvedFlat} onChange={reload} />
        )}
      </div>
    </div>
  );
}

function PendingList({ items, onChange }) {
  if (items.length === 0) {
    return (
      <div className="border border-dashed border-[#283524] py-16 text-center">
        <Check size={22} className="text-[#4ADE80] mx-auto" />
        <p className="mt-3 font-mono-ui text-xs uppercase tracking-[0.25em] text-[#8D9A87]">
          // Queue is clear.
        </p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {items.map((it) => (
        <PendingRow key={it.id} item={it} onChange={onChange} />
      ))}
    </div>
  );
}

function PendingRow({ item, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draftCaption, setDraftCaption] = useState(item.caption || "");
  const [draftYear, setDraftYear] = useState(String(item.year || ""));
  const [busy, setBusy] = useState(false);

  const act = async (action) => {
    setBusy(true);
    try {
      const body = { action };
      if (editing) {
        body.caption = draftCaption;
        const y = parseInt(draftYear, 10);
        if (!isNaN(y)) body.year = y;
      }
      await api.patch(`/admin/media/${item.id}`, body);
      toast.success(action === "approve" ? "Approved." : "Rejected.");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed.");
      setBusy(false);
    }
  };

  return (
    <div
      className="flex gap-4 border border-[#283524] bg-[#0D110C] p-4"
      data-testid={`media-pending-row-${item.id}`}
    >
      <div className="w-32 h-32 shrink-0 bg-[#151B14] overflow-hidden border border-[#283524]">
        <img
          src={resolveUrl(item.url)}
          alt={item.caption || "Pending submission"}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="flex-1 min-w-0 flex flex-col">
        <p className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#C0A875] truncate">
          {item.submitter_global_name || item.submitter_username || "Anonymous"}
        </p>
        <p className="label-tag text-[10px] text-[#5C6B51]">
          Submitted {new Date(item.submitted_at).toLocaleString()}
        </p>

        {editing ? (
          <div className="mt-2 space-y-2">
            <input
              type="text"
              value={draftCaption}
              onChange={(e) => setDraftCaption(e.target.value.slice(0, 280))}
              placeholder="Caption (optional)"
              data-testid={`media-pending-caption-input-${item.id}`}
              className="w-full bg-[#151B14] border border-[#283524] text-[#E2E4DF] px-2 py-1 font-mono-ui text-xs focus:outline-none focus:border-[#C0A875]"
            />
            <input
              type="number"
              value={draftYear}
              onChange={(e) => setDraftYear(e.target.value)}
              min="1900"
              max="2200"
              data-testid={`media-pending-year-input-${item.id}`}
              className="w-28 bg-[#151B14] border border-[#283524] text-[#E2E4DF] px-2 py-1 font-mono-ui text-xs focus:outline-none focus:border-[#C0A875]"
            />
          </div>
        ) : (
          <div className="mt-2 space-y-1">
            <p className="text-[#E2E4DF] text-sm line-clamp-3">
              {item.caption || <span className="text-[#5C6B51] italic">// no caption</span>}
            </p>
            <p className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#8D9A87]">
              Year · {item.year}
            </p>
          </div>
        )}

        <div className="mt-auto pt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => act("approve")}
            disabled={busy}
            data-testid={`media-pending-approve-${item.id}`}
            className="inline-flex items-center gap-1 border border-[#4ADE80]/60 text-[#4ADE80] px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.2em] hover:bg-[#4ADE80]/10 disabled:opacity-50"
          >
            <Check size={11} /> Approve
          </button>
          <button
            type="button"
            onClick={() => act("reject")}
            disabled={busy}
            data-testid={`media-pending-reject-${item.id}`}
            className="inline-flex items-center gap-1 border border-[#C0392B]/60 text-[#C0392B] px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.2em] hover:bg-[#C0392B]/10 disabled:opacity-50"
          >
            <X size={11} /> Reject
          </button>
          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            data-testid={`media-pending-edit-${item.id}`}
            className="inline-flex items-center gap-1 border border-[#283524] text-[#8D9A87] hover:text-[#C0A875] hover:border-[#C0A875] px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.2em] ml-auto"
          >
            <Edit3 size={11} /> {editing ? "Done" : "Edit"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ApprovedList({ items, onChange }) {
  if (items.length === 0) {
    return (
      <div className="border border-dashed border-[#283524] py-16 text-center">
        <ImageIcon size={22} className="text-[#5C6B51] mx-auto" />
        <p className="mt-3 font-mono-ui text-xs uppercase tracking-[0.25em] text-[#8D9A87]">
          // Nothing approved yet.
        </p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {items.map((it) => (
        <ApprovedCard key={it.id} item={it} onChange={onChange} />
      ))}
    </div>
  );
}

function ApprovedCard({ item, onChange }) {
  const [busy, setBusy] = useState(false);
  const del = async () => {
    if (!window.confirm("Remove this approved photo from the gallery?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/media/${item.id}`);
      toast.success("Removed.");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
      setBusy(false);
    }
  };
  return (
    <div
      className="group relative aspect-square bg-[#151B14] border border-[#283524]"
      data-testid={`media-approved-card-${item.id}`}
    >
      <img
        src={resolveUrl(item.url)}
        alt={item.caption || "Approved photo"}
        className="w-full h-full object-cover"
        loading="lazy"
      />
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <p className="font-mono-ui text-[9px] uppercase tracking-[0.2em] text-[#C0A875] truncate">
          {item.submitter_global_name || item.submitter_username || "Anonymous"} · {item.year}
        </p>
      </div>
      <button
        type="button"
        onClick={del}
        disabled={busy}
        data-testid={`media-approved-delete-${item.id}`}
        className="absolute top-1.5 right-1.5 bg-black/60 border border-[#283524] p-1 text-[#8D9A87] hover:text-[#C0392B] hover:border-[#C0392B] opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <Trash2 size={11} />
      </button>
    </div>
  );
}
